import {
  getRuntimeApiBaseUrl,
  getRuntimeClientId,
  getRuntimeClientSecret,
} from 'config/runtimeConfig';
import { AuthError, NetworkError } from 'lib/errors';

interface TokenResponse {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
}

interface CachedTokenData {
  token: string;
  expiresAt: number | null;
}

const TOKEN_EXPIRY_MARGIN_MS = 60_000;

let cachedTokenData: CachedTokenData | null = null;

const requestToken = async (): Promise<{ token: string; expiresIn: number }> => {
  const apiBaseUrl = getRuntimeApiBaseUrl();
  const clientId = getRuntimeClientId();
  const clientSecret = getRuntimeClientSecret();

  let response: Response;
  try {
    response = await fetch(`${apiBaseUrl}/authorization`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientId, clientSecret }),
    });
  } catch (err) {
    throw new NetworkError({
      message: err instanceof Error ? err.message : 'Network request failed',
    });
  }

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    // Preserve the legacy message format for back-compat with existing
    // callers and tests.
    throw new AuthError({
      status: response.status,
      message: `Token request failed with ${response.status}: ${text}`,
      body: text,
    });
  }

  const tokenResponse = (await response.json()) as TokenResponse;
  if (!tokenResponse.accessToken) {
    throw new AuthError({ message: 'Token response does not contain accessToken' });
  }

  return { token: tokenResponse.accessToken, expiresIn: tokenResponse.expiresIn };
};

export const getAuthToken = async (): Promise<string> => {
  if (cachedTokenData) {
    const now = Date.now();
    if (!cachedTokenData.expiresAt || cachedTokenData.expiresAt > now) {
      return cachedTokenData.token;
    }
  }
  const { token, expiresIn } = await requestToken();
  const expiresAt = expiresIn > 0 ? Date.now() + expiresIn * 1000 - TOKEN_EXPIRY_MARGIN_MS : null;
  cachedTokenData = { token, expiresAt };
  return token;
};

export const clearCachedToken = (): void => {
  cachedTokenData = null;
};
